import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import {
  Banknote,
  TrendingUp,
  AlertTriangle,
  Percent,
  Download,
  CalendarIcon,
  Grid3X3,
} from "lucide-react";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ManagementReportMenu } from "@/components/Holizontal/ManagementReportMenu";

/* ───────────── Types (API) ───────────── */
type BranchOpt = { id: number; name: string };
type ProductOpt = { id: number; name: string };
type BdoOpt = { id: number; name: string };

type MatrixCell = {
  rowKey: string;
  colKey: string;
  disbursed: number;
  collected: number;
  income: number;
  writeOffs: number;
  netProfit: number;
  margin: number; // %
  par: number; // %
  loanCount: number;
};

type MatrixResponse = {
  groupBy: string;
  rows: string[];
  cols: string[];
  matrix: Record<string, MatrixCell>;
  grandTotals: {
    disbursed: number;
    collected: number;
    income: number;
    writeOffs: number;
    netProfit: number;
    margin: number;
    par: number;
  };
};

type DrilldownLoan = {
  id: number;
  customer: string;
  branch: string;
  product: string;
  bdo: string;
  disbursed: number;
  collected?: number;
  income?: number;
  penalties?: number;
  writeOff?: number;
  balance?: number;
};

type DrilldownResponse = { data: DrilldownLoan[] };

/* ───────────── UI Constants ───────────── */
const groupByOptions = [
  { value: "branch_product", label: "Branch × Product" },
  { value: "branch_bdo", label: "Branch × BDO" },
  { value: "product_month", label: "Product × Month" },
  { value: "product_tenure", label: "Product × Tenure" },
] as const;

const dateFilterModes = [
  { value: "disbursement", label: "Disbursement Date" },
  { value: "payment", label: "Payment Date" }, // optional in backend later
] as const;

const fmt = (n: number) =>
  new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);

function safeNum(n: any) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

export default function ProfitabilityMatrixPage() {
  /* ───────────── Filters ───────────── */
  const [dateFrom, setDateFrom] = useState<Date | undefined>(new Date());
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date());
  const [dateMode, setDateMode] = useState<(typeof dateFilterModes)[number]["value"]>("disbursement");

  const [branchId, setBranchId] = useState<string>("All");
  const [productId, setProductId] = useState<string>("All");
  const [bdoId, setBdoId] = useState<string>("All");
  const [groupBy, setGroupBy] = useState<(typeof groupByOptions)[number]["value"]>("branch_product");

  /* ───────────── Dropdown data ───────────── */
  const [branches, setBranches] = useState<BranchOpt[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [bdos, setBdos] = useState<BdoOpt[]>([]);

  /* ───────────── Matrix data ───────────── */
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [rows, setRows] = useState<string[]>([]);
  const [cols, setCols] = useState<string[]>([]);
  const [matrix, setMatrix] = useState<Record<string, MatrixCell>>({});
  const [grandTotals, setGrandTotals] = useState<MatrixResponse["grandTotals"]>({
    disbursed: 0,
    collected: 0,
    income: 0,
    writeOffs: 0,
    netProfit: 0,
    margin: 0,
    par: 0,
  });

  /* ───────────── Drilldown ───────────── */
  const [drilldown, setDrilldown] = useState<MatrixCell | null>(null);
  const [drillLoans, setDrillLoans] = useState<DrilldownLoan[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const getCell = (r: string, c: string) => matrix[`${r}||${c}`];

  /* ───────────── Cell color scale ───────────── */
  const cellColor = (margin: number) => {
    if (margin >= 15) return "bg-emerald-500/10 border-emerald-500/25";
    if (margin >= 5) return "bg-amber-500/10 border-amber-500/25";
    return "bg-red-500/10 border-red-500/25";
  };

  /* ───────────── Date picker component ───────────── */
  const DatePick = ({
    label,
    value,
    onChange,
  }: {
    label: string;
    value?: Date;
    onChange: (d?: Date) => void;
  }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("justify-start text-left font-normal min-w-[130px]", !value && "text-muted-foreground")}
        >
          <CalendarIcon className="h-4 w-4 mr-1" />
          {value ? format(value, "dd/MM/yyyy") : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus className="p-3 pointer-events-auto" />
      </PopoverContent>
    </Popover>
  );

  /* ───────────── Load filter lists ───────────── */
  async function loadFilters() {
    try {
      const [br, pr, bd] = await Promise.all([
        apiFetch<BranchOpt[]>("/management/filters/branches"),
        apiFetch<ProductOpt[]>("/management/filters/products"),
        apiFetch<BdoOpt[]>("/management/filters/bdos"),
      ]);

      setBranches(br ?? []);
      setProducts(pr ?? []);
      setBdos(bd ?? []);
    } catch {
      // keep silent – matrix can still load even if filter lists fail
    }
  }

  /* ───────────── Load matrix ───────────── */
  async function loadMatrix() {
    setLoading(true);
    setErr(null);

    try {
      const params = new URLSearchParams();

      if (dateFrom) params.set("from", format(dateFrom, "yyyy-MM-dd"));
      if (dateTo) params.set("to", format(dateTo, "yyyy-MM-dd"));

      params.set("group_by", groupBy);
      params.set("date_mode", dateMode);

      if (branchId !== "All") params.set("branch_id", branchId);
      if (productId !== "All") params.set("product_id", productId);
      if (bdoId !== "All") params.set("bdo_id", bdoId);

      const res = await apiFetch<MatrixResponse>(`/management/profitability-matrix?${params.toString()}`);

      setRows(res.rows ?? []);
      setCols(res.cols ?? []);
      setMatrix(res.matrix ?? {});
      setGrandTotals(res.grandTotals);
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load profitability matrix");
      setRows([]);
      setCols([]);
      setMatrix({});
      setGrandTotals({ disbursed: 0, collected: 0, income: 0, writeOffs: 0, netProfit: 0, margin: 0, par: 0 });
    } finally {
      setLoading(false);
    }
  }

  /* ───────────── Drilldown loader ───────────── */
  async function openDrilldown(cell: MatrixCell) {
    setDrilldown(cell);
    setDrillLoans([]);
    setDrillLoading(true);

    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("from", format(dateFrom, "yyyy-MM-dd"));
      if (dateTo) params.set("to", format(dateTo, "yyyy-MM-dd"));

      params.set("group_by", groupBy);
      params.set("row_key", cell.rowKey);
      params.set("col_key", cell.colKey);

      const res = await apiFetch<DrilldownResponse>(`/management/profitability-matrix/drilldown?${params.toString()}`);
      setDrillLoans(res.data ?? []);
    } catch {
      setDrillLoans([]);
    } finally {
      setDrillLoading(false);
    }
  }

  /* ───────────── Initial + reactive loads ───────────── */
  useEffect(() => {
    // set default date range (start of month -> today)
    const now = new Date();
    setDateTo(now);
    setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1));

    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, dateMode, branchId, productId, bdoId, groupBy]);

  /* ───────────── Exports ───────────── */
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("Profitability Matrix Report", 14, 18);

    doc.setFontSize(9);
    const label = groupByOptions.find((g) => g.value === groupBy)?.label || groupBy;
    doc.text(
      `Grouping: ${label} | Net Profit: KES ${fmt(grandTotals.netProfit)} | Margin: ${grandTotals.margin}% | PAR: ${grandTotals.par}%`,
      14,
      25
    );

    const head = ["", ...cols, "Row Total"];
    const body = rows.map((r) => {
      const rowCells = cols.map((c) => {
        const cell = getCell(r, c);
        return cell ? `${fmt(cell.netProfit)} (${cell.margin}%)` : "-";
      });
      const rowTotal = cols.reduce((s, c) => s + safeNum(getCell(r, c)?.netProfit), 0);
      return [r, ...rowCells, fmt(rowTotal)];
    });

    autoTable(doc, { startY: 32, head: [head], body, styles: { fontSize: 7 } });
    doc.save("profitability_matrix.pdf");
  };

  const exportExcel = () => {
    const sheetData: Record<string, string | number>[] = [];
    rows.forEach((r) => {
      cols.forEach((c) => {
        const cell = getCell(r, c);
        if (cell) {
          sheetData.push({
            Row: r,
            Column: c,
            Disbursed: cell.disbursed,
            Collected: cell.collected,
            Income: cell.income,
            "Write-offs": cell.writeOffs,
            "Net Profit": cell.netProfit,
            "Margin %": cell.margin,
            "PAR %": cell.par,
            "Loans Count": cell.loanCount,
          });
        }
      });
    });

    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Matrix");
    XLSX.writeFile(wb, "profitability_matrix.xlsx");
  };

  const exportDrilldownExcel = () => {
    if (!drilldown) return;
    const ws = XLSX.utils.json_to_sheet(
      drillLoans.map((l) => ({
        LoanID: l.id,
        Customer: l.customer,
        Branch: l.branch,
        Product: l.product,
        BDO: l.bdo,
        Disbursed: safeNum(l.disbursed),
        Collected: safeNum(l.collected),
        Income: safeNum(l.income),
        Penalties: safeNum(l.penalties),
        Balance: safeNum(l.balance),
        "Write-off": safeNum(l.writeOff),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Drilldown");
    XLSX.writeFile(wb, `drilldown_${drilldown.rowKey}_${drilldown.colKey}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between gap-2"
      >
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Grid3X3 className="h-6 w-6 text-primary" /> Profitability Matrix
        </h1>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadMatrix} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </motion.div>

      <ManagementReportMenu />

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-end">
            <DatePick label="From" value={dateFrom} onChange={setDateFrom} />
            <DatePick label="To" value={dateTo} onChange={setDateTo} />

            <Select value={dateMode} onValueChange={(v) => setDateMode(v as any)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dateFilterModes.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Products</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={bdoId} onValueChange={setBdoId}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="BDO" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All BDOs</SelectItem>
                {bdos.map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as any)}>
              <SelectTrigger className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {groupByOptions.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={exportPDF}>
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={exportExcel}>
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
          </div>

          {err && (
            <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {err}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { icon: Banknote, label: "Total Disbursed", value: `KES ${fmt(grandTotals.disbursed)}`, tone: "text-blue-600" },
          { icon: Banknote, label: "Total Collected", value: `KES ${fmt(grandTotals.collected)}`, tone: "text-emerald-600" },
          { icon: TrendingUp, label: "Total Income", value: `KES ${fmt(grandTotals.income)}`, tone: "text-violet-600" },
          { icon: AlertTriangle, label: "Write-offs", value: `KES ${fmt(grandTotals.writeOffs)}`, tone: "text-red-600" },
          {
            icon: Banknote,
            label: "Net Profit",
            value: `KES ${fmt(grandTotals.netProfit)}`,
            tone: grandTotals.netProfit >= 0 ? "text-emerald-600" : "text-red-600",
          },
          {
            icon: Percent,
            label: "PAR %",
            value: `${grandTotals.par}%`,
            tone: grandTotals.par <= 5 ? "text-emerald-600" : "text-red-600",
          },
        ].map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card className="shadow-card h-full">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <c.icon className={cn("h-4 w-4", c.tone)} />
                  <span className="text-xs text-muted-foreground">{c.label}</span>
                </div>
                <p className="text-lg font-bold">{c.value}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Matrix Grid */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>{groupByOptions.find((g) => g.value === groupBy)?.label} Matrix</span>
            {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0 overflow-x-auto">
          {/* Desktop */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card z-10">
                    {groupBy.includes("branch") ? "Branch" : "Product"}
                  </TableHead>
                  {cols.map((c) => (
                    <TableHead key={c} className="text-center min-w-[150px]">
                      {c}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={cols.length + 1} className="py-10 text-center text-muted-foreground">
                      No data found for selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r}>
                      <TableCell className="font-medium sticky left-0 bg-card z-10">{r}</TableCell>

                      {cols.map((c) => {
                        const cell = getCell(r, c);
                        if (!cell) {
                          return (
                            <TableCell key={c} className="text-center text-muted-foreground">
                              -
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={c} className="p-1">
                            <button
                              onClick={() => openDrilldown(cell)}
                              className={cn(
                                "w-full rounded-lg border p-3 text-center transition hover:opacity-90",
                                cellColor(cell.margin)
                              )}
                            >
                              <p className="text-sm font-bold">KES {fmt(cell.netProfit)}</p>
                              <p className="text-[11px] text-muted-foreground">Margin: {cell.margin}%</p>
                              <p className="text-[11px] text-muted-foreground">PAR: {cell.par}%</p>
                              <div className="mt-1">
                                <Badge variant="outline" className="text-[10px]">
                                  Loans: {cell.loanCount}
                                </Badge>
                              </div>
                            </button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-3 p-4">
            {rows.length === 0 && !loading ? (
              <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
                No data found for selected filters.
              </div>
            ) : (
              rows.map((r) => (
                <Card key={r} className="shadow-card">
                  <CardContent className="p-4 space-y-2">
                    <p className="font-semibold text-sm">{r}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {cols.map((c) => {
                        const cell = getCell(r, c);
                        if (!cell) return null;

                        return (
                          <button
                            key={c}
                            onClick={() => openDrilldown(cell)}
                            className={cn("rounded-lg border p-3 text-left transition hover:opacity-90", cellColor(cell.margin))}
                          >
                            <p className="text-xs font-medium text-muted-foreground">{c}</p>
                            <p className="text-sm font-bold">KES {fmt(cell.netProfit)}</p>
                            <p className="text-xs text-muted-foreground">M:{cell.margin}% · PAR:{cell.par}%</p>
                            <Badge variant="outline" className="mt-1 text-[10px]">
                              Loans: {cell.loanCount}
                            </Badge>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500/20 border border-emerald-500/30" />
          <span>High Profit (≥15%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-amber-500/15 border border-amber-500/30" />
          <span>Low Profit (5–15%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-500/15 border border-red-500/30" />
          <span>Loss / High Risk (&lt;5%)</span>
        </div>
      </div>

      {/* Drilldown Sheet */}
      <Sheet open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {drilldown && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {drilldown.rowKey} × {drilldown.colKey}
                </SheetTitle>
              </SheetHeader>

              <div className="mt-4 space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { l: "Disbursed", v: fmt(drilldown.disbursed) },
                    { l: "Collected", v: fmt(drilldown.collected) },
                    { l: "Income", v: fmt(drilldown.income) },
                    { l: "Write-offs", v: fmt(drilldown.writeOffs) },
                    { l: "Net Profit", v: fmt(drilldown.netProfit) },
                    { l: "Margin", v: `${drilldown.margin}%` },
                  ].map((s) => (
                    <div key={s.l} className="rounded-lg border border-border p-3">
                      <p className="text-xs text-muted-foreground">{s.l}</p>
                      <p className="text-sm font-bold">KES {s.v}</p>
                    </div>
                  ))}
                </div>

                {/* Loans */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Loans ({drillLoans.length})
                    {drillLoading && <span className="ml-2 text-xs text-muted-foreground">Loading…</span>}
                  </p>

                  <Button variant="outline" size="sm" onClick={exportDrilldownExcel} disabled={drillLoading}>
                    <Download className="h-4 w-4 mr-2" /> Export
                  </Button>
                </div>

                <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                  {drillLoans.length === 0 && !drillLoading ? (
                    <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground text-center">
                      No loans found for this cell.
                    </div>
                  ) : (
                    drillLoans.map((l) => (
                      <div key={l.id} className="rounded-xl border border-border p-3 text-sm space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{l.customer ?? "-"}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {l.branch} • {l.bdo}
                            </div>
                          </div>
                          <Badge variant="outline">{l.product}</Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                          <span>Loan ID: {l.id}</span>
                          <span>Disbursed: KES {fmt(safeNum(l.disbursed))}</span>
                          <span>Collected: KES {fmt(safeNum(l.collected))}</span>
                          <span>Income: KES {fmt(safeNum(l.income))}</span>
                          {safeNum(l.penalties) > 0 && <span>Penalties: KES {fmt(safeNum(l.penalties))}</span>}
                          {safeNum(l.balance) > 0 && <span>Balance: KES {fmt(safeNum(l.balance))}</span>}
                          {safeNum(l.writeOff) > 0 && (
                            <span className="text-red-600">Write-off: KES {fmt(safeNum(l.writeOff))}</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
